'use client';

import { useId, useState, type FormEvent } from 'react';

import { AccountField } from '@/components/platform/AccountField';
import { SlideOver } from '@/components/admin/SlideOver';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { shortDate } from '@/components/platform/format';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import {
  ACCOUNT_EMAIL_MAX,
  ACCOUNT_NAME_MAX,
  ACCOUNT_PASSWORD_MAX,
  ACCOUNT_PASSWORD_MIN,
  accountError,
  accountFieldError,
  leaveIfSignedOut,
  parsePlatformAccount,
  parsePlatformAccountPage,
  type PlatformAccount,
} from '@/lib/platform/accounts';
import { listPath } from '@/lib/query/page';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useDebouncedSearch, useSectionParams } from '@/lib/query/table-params';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { RecordPager } from '@/components/admin/RecordPager';

const PASSWORD_HINT = 'At least 12 characters. It is not shown again.';

export function PlatformUsersPage() {
  const table = useSectionParams('accounts');
  const search = useDebouncedSearch('accounts', table.search, table.write);
  const invalidate = useInvalidateTenantResource();
  const accountQuery = useTenantQuery(
    ['platform-users', table.page, table.search, table.sort, table.direction],
    listPath('/api/platform/users', {
      page: table.page,
      perPage: 20,
      sort: table.sort || 'name',
      direction: table.direction || 'asc',
      q: table.search,
    }),
    parsePlatformAccountPage,
  );
  const rows = accountQuery.data?.data ?? null;
  const total = accountQuery.data?.meta.total ?? 0;
  const error = accountQuery.isError && !accountQuery.data
    ? accountError(accountQuery.error, 'Could not load platform accounts.')
    : null;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlatformAccount | null>(null);
  const [activeTarget, setActiveTarget] = useState<PlatformAccount | null>(null);
  const [activePending, setActivePending] = useState(false);
  const [activeError, setActiveError] = useState<string | null>(null);

  async function saveAccount() {
    await invalidate('platform-users');
  }

  async function changeActive() {
    if (!activeTarget) return;
    setActivePending(true);
    setActiveError(null);
    try {
      const updated = parsePlatformAccount(
        await api<unknown>(`/api/platform/users/${activeTarget.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ active: !activeTarget.active }),
        }),
      );
      await saveAccount();
      setActiveTarget(null);
      notify(updated.active ? 'Account restored.' : 'Account suspended.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setActiveError(accountError(err, 'Could not update this account.'));
    } finally {
      setActivePending(false);
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xl text-sm text-muted">These accounts can open this console. A tenant role cannot grant this access.</p>
        <input
          value={search.draft}
          onChange={(event) => search.onSearch(event.target.value)}
          placeholder="Search name or email"
          aria-label="Search platform accounts"
          className="w-full max-w-xs rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
        />
        <button
          type="button"
          disabled={rows === null}
          onClick={() => setCreating(true)}
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50"
        >
          Create account
        </button>
      </div>

      {error && <LoadError message={error} onRetry={() => { void accountQuery.refetch(); }} />}
      {accountQuery.isLoading && !error && <AccountSkeleton />}
      {rows?.length === 0 && table.search === '' && <EmptyAccounts onCreate={() => setCreating(true)} />}
      {rows?.length === 0 && table.search !== '' && (
        <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No account matches that search.</p>
      )}
      {rows && rows.length > 0 && (
        <AccountTable
          rows={rows}
          onEdit={setEditing}
          onActive={(account) => {
            setActiveError(null);
            setActiveTarget(account);
          }}
        />
      )}

      {creating && (
        <CreateAccount
          onClose={() => setCreating(false)}
          onCreated={(account) => {
            void saveAccount();
            setCreating(false);
            notify('Account created.');
          }}
        />
      )}
      {editing && (
        <EditAccount
          account={editing}
          onClose={() => setEditing(null)}
          onSaved={(account) => {
            void saveAccount();
            setEditing(null);
            notify('Account updated.');
          }}
        />
      )}
      <ConfirmDialog
        open={activeTarget !== null}
        title={activeTarget?.active ? 'Suspend this platform owner?' : 'Restore this platform owner?'}
        description={
          activeTarget?.active
            ? `${activeTarget.name} will not be able to sign in. The platform must keep one active owner.`
            : `${activeTarget?.name ?? 'This account'} will be able to sign in again.`
        }
        confirmLabel={activeTarget?.active ? 'Suspend' : 'Restore'}
        pending={activePending}
        error={activeError}
        onCancel={() => {
          if (activePending) return;
          setActiveTarget(null);
          setActiveError(null);
        }}
        onConfirm={() => void changeActive()}
      />
      <RecordPager
        page={accountQuery.data?.meta.page ?? table.page}
        pages={Math.max(1, Math.ceil(total / (accountQuery.data?.meta.perPage || 20)))}
        total={rows === null ? -1 : total}
        onPage={(next) => table.write({ page: String(next) }, false)}
      />
    </section>
  );
}

function AccountTable({
  rows,
  onEdit,
  onActive,
}: {
  rows: PlatformAccount[];
  onEdit: (account: PlatformAccount) => void;
  onActive: (account: PlatformAccount) => void;
}) {
  return (
    <div className="max-h-[70vh] overflow-auto rounded-2xl border border-line bg-card">
      <table className="min-w-full border-separate border-spacing-0">
        <thead className="sticky top-0 z-10 bg-card">
          <tr className="type-table-header text-left text-muted">
            <th className="border-b border-line px-4 py-3 font-semibold">Name</th>
            <th className="border-b border-line px-4 py-3 font-semibold">Email</th>
            <th className="border-b border-line px-4 py-3 font-semibold">Status</th>
            <th className="border-b border-line px-4 py-3 font-semibold">Created</th>
            <th className="border-b border-line px-4 py-3 font-semibold">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((account) => (
            <tr key={account.id} className="type-table-cell text-ink">
              <td className="border-b border-line px-4 py-3 font-semibold">{account.name}</td>
              <td className="border-b border-line px-4 py-3 text-muted">{account.email}</td>
              <td className="border-b border-line px-4 py-3">
                <StatusPill active={account.active} />
              </td>
              <td className="border-b border-line px-4 py-3 text-muted">{account.createdAt ? shortDate(account.createdAt) : '—'}</td>
              <td className="border-b border-line px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => onEdit(account)} className="rounded-full border border-line px-3 py-1 text-xs font-semibold text-ink">
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onActive(account)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${account.active ? 'text-danger' : 'text-success'}`}
                  >
                    {account.active ? 'Suspend' : 'Restore'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateAccount({ onClose, onCreated }: { onClose: () => void; onCreated: (account: PlatformAccount) => void }) {
  const formId = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const invalid = accountFieldError({ name, email, password, passwordRequired: true });
    if (invalid) {
      setError(invalid);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const created = parsePlatformAccount(
        await api<unknown>('/api/platform/users', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), email: email.trim(), password }),
        }),
      );
      onCreated(created);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not create the account.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver
      open
      title="Create platform account"
      onClose={onClose}
      footer={<SaveButton form={formId} pending={pending} label="Create account" />}
    >
      <form id={formId} onSubmit={(event) => void submit(event)} className="space-y-4">
        <AccountField label="Name" value={name} onChange={setName} autoComplete="name" maxLength={ACCOUNT_NAME_MAX} />
        <AccountField label="Email" value={email} onChange={setEmail} type="email" autoComplete="off" maxLength={ACCOUNT_EMAIL_MAX} />
        <AccountField
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
          minLength={ACCOUNT_PASSWORD_MIN}
          maxLength={ACCOUNT_PASSWORD_MAX}
          hint={PASSWORD_HINT}
        />
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}

function EditAccount({
  account,
  onClose,
  onSaved,
}: {
  account: PlatformAccount;
  onClose: () => void;
  onSaved: (account: PlatformAccount) => void;
}) {
  const formId = useId();
  const [name, setName] = useState(account.name);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const invalid = accountFieldError({ name, email: account.email, password, passwordRequired: false });
    if (invalid) {
      setError(invalid);
      return;
    }
    setPending(true);
    setError(null);
    const body: { name: string; password?: string } = { name: name.trim() };
    if (password !== '') body.password = password;
    try {
      const updated = parsePlatformAccount(
        await api<unknown>(`/api/platform/users/${account.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      );
      onSaved(updated);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not update the account.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver
      open
      title={`Edit ${account.name}`}
      onClose={onClose}
      footer={<SaveButton form={formId} pending={pending} label="Save changes" />}
    >
      <form id={formId} onSubmit={(event) => void submit(event)} className="space-y-4">
        <p className="text-xs text-muted">{account.email}</p>
        <AccountField label="Name" value={name} onChange={setName} autoComplete="name" maxLength={ACCOUNT_NAME_MAX} />
        <AccountField
          label="New password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
          required={false}
          maxLength={ACCOUNT_PASSWORD_MAX}
          hint="Leave blank to keep the current password."
        />
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}

function SaveButton({ form, pending, label }: { form: string; pending: boolean; label: string }) {
  return (
    <button type="submit" form={form} disabled={pending} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">
      {pending ? 'Saving…' : label}
    </button>
  );
}

function StatusPill({ active }: { active: boolean }) {
  const tone = active ? 'border-success/30 bg-success/10 text-success' : 'border-danger/30 bg-danger/10 text-danger';
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>{active ? 'Active' : 'Suspended'}</span>;
}

function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-danger/40 bg-card px-4 py-6">
      <p className="text-sm text-danger">{message}</p>
      <button type="button" onClick={onRetry} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
        Try again
      </button>
    </div>
  );
}

function EmptyAccounts({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-line bg-card px-6 py-10 text-center">
      <p className="text-sm font-semibold text-ink">No platform owners yet</p>
      <p className="mt-1 text-xs text-muted">Create the next account that can open this console.</p>
      <button type="button" onClick={onCreate} className="mt-4 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">
        Create account
      </button>
    </div>
  );
}

function AccountSkeleton() {
  return (
    <div className="space-y-2 rounded-2xl border border-line bg-card p-4" aria-busy="true" aria-live="polite">
      <p className="sr-only">Loading platform accounts</p>
      {['one', 'two', 'three', 'four'].map((key) => (
        <div key={key} className="h-10 animate-pulse rounded-xl bg-shell" />
      ))}
    </div>
  );
}
